import React, { useEffect } from 'react';
import { toast } from 'sonner';
import { Placeholder, Card } from 'react-bootstrap';
import { format } from 'date-fns';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import useRevenueMonthly from '../../../../services/cm-room-report/hooks/useRevenueMonthly';

interface RevenueMonthlyHighchartsProps {
    propertyId: string | undefined;
    startDate: Date;
    endDate: Date;
}

const RevenueMonthlyHighcharts: React.FC<RevenueMonthlyHighchartsProps> = ({ propertyId, startDate, endDate }) => {
    const { data: revenueData, isFetching } = useRevenueMonthly({
        filter: {
            property_id: propertyId || "",
            start_date: format(startDate, 'yyyy-MM-dd'),
            end_date: format(endDate, 'yyyy-MM-dd')
        }
    });

    // Toast notifikasi saat data berhasil di-load
    useEffect(() => {
        if (!isFetching && revenueData) {
            toast.success('Revenue data loaded successfully.');
        }
    }, [revenueData, isFetching]);

    // 1. Transformasi data untuk Highcharts
    // Urutkan data berdasarkan bulan untuk memastikan urutan yang benar
    const sortedData = revenueData?.data.sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()) || [];

    // Pisahkan kategori (sumbu X) dan data series (sumbu Y)
    const categories = sortedData.map(item =>
        new Date(item.month).toLocaleString('default', { month: 'short', year: '2-digit' })
    );
    const seriesData = sortedData.map(item => item.rates);

    // 2. Konfigurasi opsi untuk Highcharts
    const getOptions = (): Highcharts.Options => ({
        chart: {
            type: 'spline',
            backgroundColor: 'transparent'
        },
        title: {
            text: 'Graph of Night Revenue in Current Year'
        },
        xAxis: {
            categories: categories,
            title: {
                text: 'Month'
            }
        },
        yAxis: {
            title: {
                text: 'Revenue'
            }
        },
        tooltip: {
            shared: true,
            valuePrefix: 'IDR ' // Anda bisa menyesuaikan unit mata uang di sini
        },
        legend: {
            enabled: true
        },
        plotOptions: {
            spline: {
                marker: {
                    enabled: true,
                    symbol: 'circle',
                    radius: 4
                }
            }
        },
        series: [{
            type: 'spline',
            name: 'Revenue',
            data: seriesData,
            color: '#1E90FF' // Warna sama seperti di Recharts
        }],
        credits: {
            enabled: false
        }
    });

    return (
        <Card className="h-100 shadow">
            <Card.Body>
                {isFetching ? (
                    <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Placeholder as="div" animation="wave" style={{ width: '100%' }}>
                            <Placeholder xs={12} style={{ height: 300, borderRadius: 10, backgroundColor: '#e0e0e0' }} />
                        </Placeholder>
                    </div>
                ) : (
                    <HighchartsReact
                        highcharts={Highcharts}
                        options={getOptions()}
                        containerProps={{ style: { height: '300px', width: '100%' } }}
                    />
                )}
            </Card.Body>
        </Card>
    );
}

export default RevenueMonthlyHighcharts;